import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  TablePagination,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
  TextField,
  Switch,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Description as DescriptionIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { getQuestionAnswers, patchQuestionAnswer } from '../utils/questionnaireApi';
import { CopyAnswerWithOriginalMenu } from './CopyAnswerWithOriginalMenu';
import { QuestionEnglishInline } from './QuestionEnglishInline';
import {
  formatDate,
  getRatingIcon,
  cleanQuestionNumber,
  cleanQuestionText,
  getDisplayAnswer,
  formatRetrievalDistance,
  averageRetrievalDistanceLabel,
  sortRetrievalSourcesByDistance,
  RETRIEVAL_DISTANCE_TOOLTIP,
  AVERAGE_RETRIEVAL_DISTANCE_TOOLTIP,
  RETRIEVAL_SCORE_LEGEND,
} from '../utils/qaHelpers';
import { QAItem, FilterType } from '../types/qa';
import { PageLayout } from '../../../shared/components';
import './ReviewAnswers.css';

// Custom Archive Icon SVG
const ArchiveIconSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M22 20V7L20 3H4L2 7.00353V20C2 20.5523 2.44772 21 3 21H21C21.5523 21 22 20.5523 22 20ZM4 9H20V19H4V9ZM5.236 5H18.764L19.764 7H4.237L5.236 5ZM15 11H9V13H15V11Z"></path>
  </svg>
);

const ReviewAnswers: React.FC = () => {
  const [qaItems, setQaItems] = useState<QAItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [activeFilter, setActiveFilter] = useState<FilterType>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveItem, setArchiveItem] = useState<{ qafId: string; itemId: number } | null>(null);
  const [approvedItems, setApprovedItems] = useState<Set<number>>(new Set());
  const [editModes, setEditModes] = useState<{ [key: number]: boolean }>({});
  const [editedAnswers, setEditedAnswers] = useState<{ [key: number]: string }>({});
  const [savingAnswers, setSavingAnswers] = useState<{ [key: number]: boolean }>({});

  const fetchQAData = useCallback(async (pageNum: number, pageSize: number, filter: FilterType) => {
    setLoading(true);
    setError(null);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: (pageNum + 1).toString(),
        page_size: pageSize.toString(),
      });

      // Determine if we should exclude admin statuses (for todo list view)
      const isAdminStatusFilter = filter === 'approved' || filter === 'archived'; // || filter === 'reviewed' (commented out)
      
      // Add filter parameters based on active filter
      if (filter) {
        switch (filter) {
          case 'liked':
            params.append('feedback_rating', 'LIKED');
            break;
          case 'rejected':
            params.append('feedback_rating', 'DISLIKED');
            break;
          case 'edited':
            params.append('has_edited_ans', 'true');
            break;
          case 'approved':
            params.append('admin_status', 'APPROVED');
            break;
          // case 'reviewed':
          //   params.append('admin_status', 'REVIEWED');
          //   break;
          case 'archived':
            params.append('admin_status', 'ARCHIVED');
            break;
        }
      }
      
      // By default (no filter or non-admin-status filter), exclude approved/reviewed/archived for todo list view
      if (!isAdminStatusFilter) {
        params.append('exclude_admin_status', 'APPROVED');
        params.append('exclude_admin_status', 'REVIEWED');
        params.append('exclude_admin_status', 'ARCHIVED');
      }

      const data = await getQuestionAnswers(params);
      setQaItems(data.items);
      setTotal(data.total);
      // Update approved items set based on current data
      const approvedIds = new Set(
        data.items
          .filter(item => item.admin_status?.toUpperCase() === 'APPROVED')
          .map(item => item.id)
      );
      setApprovedItems(approvedIds);
    } catch (err) {
      setError('Failed to load questions. Please try again.');
      console.error('Error fetching QA data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQAData(page, rowsPerPage, activeFilter);
  }, [page, rowsPerPage, activeFilter, fetchQAData]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterClick = (filter: FilterType) => {
    const newFilter = activeFilter === filter ? null : filter;
    setActiveFilter(newFilter);
    setPage(0); // Reset to first page when filter changes
  };

  const handleResetAll = () => {
    setActiveFilter(null);
    setPage(0); // Reset to first page when resetting filters
  };

  const handleUpdateAdminStatus = async (qafId: string, status: 'APPROVED' | 'REVIEWED' | 'ARCHIVED', itemId: number) => {
    setUpdatingId(itemId);
    try {
      await patchQuestionAnswer(qafId, { admin_status: status });
      // Refresh data after update with current filter
      await fetchQAData(page, rowsPerPage, activeFilter);
    } catch (err) {
      setError(`Failed to update status to ${status}. Please try again.`);
      console.error(`Error updating status to ${status}:`, err);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleApprove = async (qafId: string, itemId: number) => {
    await handleUpdateAdminStatus(qafId, 'APPROVED', itemId);
    setToastMessage('Item approved successfully');
    setApprovedItems(prev => new Set(prev).add(itemId));
  };

  const handleDisapprove = async (qafId: string, itemId: number) => {
    setUpdatingId(itemId);
    try {
      await patchQuestionAnswer(qafId, { admin_status: null });
      // Refresh data after update with current filter
      await fetchQAData(page, rowsPerPage, activeFilter);
      setToastMessage('Item disapproved successfully');
      setApprovedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    } catch (err) {
      setError('Failed to disapprove item. Please try again.');
      console.error('Error disapproving item:', err);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleApproveCheckboxChange = (itemId: number, qafId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      handleApprove(qafId, itemId);
    }
  };

  const handleDisapproveCheckboxChange = (itemId: number, qafId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.checked) {
      handleDisapprove(qafId, itemId);
    }
  };

  // const handleReview = async (qafId: string, itemId: number) => {
  //   // Disabled for now
  //   // await handleUpdateAdminStatus(qafId, 'REVIEWED', itemId);
  // };

  const handleArchiveClick = (qafId: string, itemId: number) => {
    setArchiveItem({ qafId, itemId });
    setArchiveDialogOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (archiveItem) {
      await handleUpdateAdminStatus(archiveItem.qafId, 'ARCHIVED', archiveItem.itemId);
      setArchiveDialogOpen(false);
      setArchiveItem(null);
    }
  };

  const handleArchiveCancel = () => {
    setArchiveDialogOpen(false);
    setArchiveItem(null);
  };

  const handleEditModeToggle = (itemId: number, currentAnswer: string) => {
    setEditModes((prev) => {
      const newModes = { ...prev };
      if (newModes[itemId]) {
        // Exiting edit mode - reset edited answer
        setEditedAnswers((prevAnswers) => {
          const newAnswers = { ...prevAnswers };
          delete newAnswers[itemId];
          return newAnswers;
        });
        delete newModes[itemId];
      } else {
        // Entering edit mode - initialize with current answer
        newModes[itemId] = true;
        setEditedAnswers((prevAnswers) => ({
          ...prevAnswers,
          [itemId]: currentAnswer,
        }));
      }
      return newModes;
    });
  };

  const handleAnswerChange = (itemId: number, value: string) => {
    setEditedAnswers((prev) => ({
      ...prev,
      [itemId]: value,
    }));
  };

  const handleSaveAnswer = async (item: QAItem) => {
    const editedAnswer = editedAnswers[item.id];
    if (!editedAnswer || editedAnswer === getDisplayAnswer(item)) {
      // No changes or empty, just exit edit mode
      setEditModes((prev) => {
        const newModes = { ...prev };
        delete newModes[item.id];
        return newModes;
      });
      return;
    }

    setSavingAnswers((prev) => ({ ...prev, [item.id]: true }));
    try {
      await patchQuestionAnswer(item.qaf_id, { admin_edited_ans: editedAnswer });
      // Refresh data after update
      await fetchQAData(page, rowsPerPage, activeFilter);
      setToastMessage('Answer saved successfully');
      setEditModes((prev) => {
        const newModes = { ...prev };
        delete newModes[item.id];
        return newModes;
      });
    } catch (err) {
      setError('Failed to save answer. Please try again.');
      console.error('Error saving answer:', err);
    } finally {
      setSavingAnswers((prev) => {
        const newSaving = { ...prev };
        delete newSaving[item.id];
        return newSaving;
      });
    }
  };

  return (
    <PageLayout>
      {/* Title and Instructions */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: '2rem',
              color: '#333',
              mb: 1.5,
              fontFamily: '"Bitter", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            }}
          >
            Review answers
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#666',
              fontSize: '1rem',
              mb: 0.5,
            }}
          >
            Review answers that need attention: liked, edited, or disliked items.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#666',
              fontSize: '1rem',
            }}
          >
            Select an item to approve, review, or edit the answers.
          </Typography>
        </Box>

        {/* Filter Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<ThumbUpIcon />}
            onClick={() => handleFilterClick('liked')}
            sx={{
              minHeight: '36px',
              px: 2,
              border: activeFilter === 'liked' ? '1.5px solid #156C86' : '1px solid #d9dede',
              backgroundColor: activeFilter === 'liked' ? 'rgba(71, 161, 173, 0.1)' : '#ffffff',
              color: activeFilter === 'liked' ? '#156C86' : '#333333',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              borderRadius: '40px',
              boxShadow: 'none',
              '& .MuiButton-startIcon': {
                color: activeFilter === 'liked' ? '#47A1AD' : '#999999',
              },
              '&:hover': {
                border: activeFilter === 'liked' ? '1.5px solid #156C86' : '1px solid #666666',
                backgroundColor: activeFilter === 'liked' ? 'rgba(71, 161, 173, 0.1)' : 'rgba(71, 161, 173, 0.1)',
                color: activeFilter === 'liked' ? '#156C86' : '#212121',
                boxShadow: 'none',
                '& .MuiButton-startIcon': {
                  color: activeFilter === 'liked' ? '#47A1AD' : '#666666',
                },
              },
            }}
          >
            Liked
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => handleFilterClick('edited')}
            sx={{
              minHeight: '36px',
              px: 2,
              border: activeFilter === 'edited' ? '1.5px solid #156C86' : '1px solid #d9dede',
              backgroundColor: activeFilter === 'edited' ? 'rgba(71, 161, 173, 0.1)' : '#ffffff',
              color: activeFilter === 'edited' ? '#156C86' : '#333333',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              borderRadius: '40px',
              boxShadow: 'none',
              '& .MuiButton-startIcon': {
                color: activeFilter === 'edited' ? '#47A1AD' : '#999999',
              },
              '&:hover': {
                border: activeFilter === 'edited' ? '1.5px solid #156C86' : '1px solid #666666',
                backgroundColor: activeFilter === 'edited' ? 'rgba(71, 161, 173, 0.1)' : 'rgba(71, 161, 173, 0.1)',
                color: activeFilter === 'edited' ? '#156C86' : '#212121',
                boxShadow: 'none',
                '& .MuiButton-startIcon': {
                  color: activeFilter === 'edited' ? '#47A1AD' : '#666666',
                },
              },
            }}
          >
            Edited
          </Button>
          <Button
            variant="outlined"
            startIcon={<ThumbDownIcon />}
            onClick={() => handleFilterClick('rejected')}
            sx={{
              minHeight: '36px',
              px: 2,
              border: activeFilter === 'rejected' ? '1.5px solid #C42E30' : '1px solid #d9dede',
              backgroundColor: activeFilter === 'rejected' ? 'rgba(196, 46, 48, 0.1)' : '#ffffff',
              color: activeFilter === 'rejected' ? '#C42E30' : '#333333',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              borderRadius: '40px',
              boxShadow: 'none',
              '& .MuiButton-startIcon': {
                color: activeFilter === 'rejected' ? '#C42E30' : '#999999',
              },
              '&:hover': {
                border: activeFilter === 'rejected' ? '1.5px solid #C42E30' : '1px solid #666666',
                backgroundColor: activeFilter === 'rejected' ? 'rgba(196, 46, 48, 0.1)' : 'rgba(196, 46, 48, 0.1)',
                color: activeFilter === 'rejected' ? '#C42E30' : '#212121',
                boxShadow: 'none',
                '& .MuiButton-startIcon': {
                  color: activeFilter === 'rejected' ? '#C42E30' : '#666666',
                },
              },
            }}
          >
            Disliked
          </Button>
          <Button
            variant="outlined"
            startIcon={<CheckCircleIcon />}
            onClick={() => handleFilterClick('approved')}
            sx={{
              minHeight: '36px',
              px: 2,
              border: activeFilter === 'approved' ? '1.5px solid #6B9E78' : '1px solid #d9dede',
              backgroundColor: activeFilter === 'approved' ? 'rgba(107, 158, 120, 0.1)' : '#ffffff',
              color: activeFilter === 'approved' ? '#6B9E78' : '#333333',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              borderRadius: '40px',
              boxShadow: 'none',
              '& .MuiButton-startIcon': {
                color: activeFilter === 'approved' ? '#6B9E78' : '#999999',
              },
              '&:hover': {
                border: activeFilter === 'approved' ? '1.5px solid #6B9E78' : '1px solid #666666',
                backgroundColor: activeFilter === 'approved' ? 'rgba(107, 158, 120, 0.1)' : 'rgba(107, 158, 120, 0.1)',
                color: activeFilter === 'approved' ? '#6B9E78' : '#212121',
                boxShadow: 'none',
                '& .MuiButton-startIcon': {
                  color: activeFilter === 'approved' ? '#6B9E78' : '#666666',
                },
              },
            }}
          >
            Approved
          </Button>
          {/* Reviewed filter button - commented out for now */}
          {/* <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => handleFilterClick('reviewed')}
            sx={{
              minHeight: '36px',
              px: 2,
              border: activeFilter === 'reviewed' ? '1.5px solid #48a1ad' : '1px solid #d9dede',
              backgroundColor: activeFilter === 'reviewed' ? '#e0f4f6' : '#ffffff',
              color: activeFilter === 'reviewed' ? '#48a1ad' : '#333333',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              borderRadius: '40px',
              boxShadow: 'none',
              '&:hover': {
                border: '1.5px solid #48a1ad',
                backgroundColor: activeFilter === 'reviewed' ? '#d0eef1' : '#f5f5f5',
                boxShadow: 'none',
              },
            }}
          >
            Reviewed
          </Button> */}
          <Button
            variant="outlined"
            startIcon={<ArchiveIconSVG />}
            onClick={() => handleFilterClick('archived')}
            sx={{
              minHeight: '36px',
              px: 2,
              border: activeFilter === 'archived' ? '1.5px solid #156C86' : '1px solid #d9dede',
              backgroundColor: activeFilter === 'archived' ? 'rgba(71, 161, 173, 0.1)' : '#ffffff',
              color: activeFilter === 'archived' ? '#156C86' : '#333333',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              borderRadius: '40px',
              boxShadow: 'none',
              '& .MuiButton-startIcon': {
                color: activeFilter === 'archived' ? '#47A1AD' : '#999999',
              },
              '&:hover': {
                border: activeFilter === 'archived' ? '1.5px solid #156C86' : '1px solid #666666',
                backgroundColor: activeFilter === 'archived' ? 'rgba(71, 161, 173, 0.1)' : 'rgba(71, 161, 173, 0.1)',
                color: activeFilter === 'archived' ? '#156C86' : '#212121',
                boxShadow: 'none',
                '& .MuiButton-startIcon': {
                  color: activeFilter === 'archived' ? '#47A1AD' : '#666666',
                },
              },
            }}
          >
            Archived
          </Button>
          <Button
            variant="text"
            onClick={handleResetAll}
            sx={{
              minHeight: '36px',
              px: 1,
              color: '#333333',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              borderRadius: '40px',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
            }}
          >
            Reset all
          </Button>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontSize: '0.95rem',
              },
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Questions List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : qaItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body2" color="textSecondary">
              No questions found
            </Typography>
          </Box>
        ) : (
          <>
            {/* Top Pagination */}
            <Paper
              elevation={0}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 0,
                backgroundColor: '#ffffff',
                mb: 2,
              }}
            >
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    px: 2,
                    py: 1,
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.875rem',
                  },
                }}
              />
            </Paper>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              {qaItems.map((item) => {
                const avgRetrievalDistance = averageRetrievalDistanceLabel(item.retrieval_sources);
                const sortedRetrievalSources = sortRetrievalSourcesByDistance(item.retrieval_sources);
                return (
                <Card
                  key={item.id}
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: '#ffffff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    {/* Header with metadata */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      mb: 2,
                      pb: 1.5,
                      borderBottom: '1px solid #e0e0e0',
                    }}>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Chip
                          label={formatDate(item.question_created_at)}
                          size="small"
                          className="common-chip"
                          sx={{
                            backgroundColor: '#f5f5f5',
                            color: '#666666',
                          }}
                        />
                        {item.client_name && (
                          <Chip
                            label={item.client_name}
                            size="small"
                            className="common-chip"
                            sx={{
                              backgroundColor: '#f5f5f5',
                              color: '#666666',
                            }}
                          />
                        )}
                        {item.user_email && (
                          <Chip
                            label={item.user_email}
                            size="small"
                            className="common-chip"
                            sx={{
                              backgroundColor: '#f5f5f5',
                              color: '#666666',
                            }}
                          />
                        )}
                        {item.feedback_rating && getRatingIcon(item.feedback_rating) && (() => {
                          const normalizedRating = item.feedback_rating.toUpperCase();
                          const isLiked = normalizedRating === 'LIKED' || normalizedRating === 'POSITIVE';
                          const isDisliked = normalizedRating === 'DISLIKED' || normalizedRating === 'NEGATIVE';
                          const label = isLiked ? 'Liked' : isDisliked ? 'Disliked' : item.feedback_rating.charAt(0).toUpperCase() + item.feedback_rating.slice(1).toLowerCase();
                          
                          return (
                            <Chip
                              icon={getRatingIcon(item.feedback_rating) || undefined}
                              label={label}
                              size="small"
                              className="common-chip"
                              variant="filled"
                              sx={{
                                backgroundColor: isLiked ? 'rgba(71, 161, 173, 0.1)' : 'rgba(196, 46, 48, 0.1)',
                                color: isLiked ? '#156C86' : '#C42E30',
                                border: 'none',
                                '& .MuiChip-icon': {
                                  color: isLiked ? '#47A1AD' : '#C42E30',
                                },
                              }}
                            />
                          );
                        })()}
                        {item.admin_status && (() => {
                          const status = item.admin_status.toUpperCase();
                          let chipColor = '#666666';
                          let chipBg = '#f5f5f5';
                          let label = item.admin_status;
                          
                          if (status === 'APPROVED') {
                            chipColor = '#6B9E78';
                            chipBg = 'rgba(107, 158, 120, 0.1)';
                            label = 'Approved';
                          } else if (status === 'REVIEWED') {
                            chipColor = '#156C86';
                            chipBg = 'rgba(71, 161, 173, 0.1)';
                            label = 'Reviewed';
                          } else if (status === 'ARCHIVED') {
                            chipColor = '#156C86';
                            chipBg = 'rgba(71, 161, 173, 0.1)';
                            label = 'Archived';
                          }
                          
                          return (
                            <Chip
                              label={label}
                              size="small"
                              className="common-chip"
                              variant="filled"
                              sx={{
                                backgroundColor: chipBg,
                                color: chipColor,
                                border: 'none',
                              }}
                            />
                          );
                        })()}
                      </Box>
                    </Box>

                    {/* Question and Answers Accordion */}
                    <Accordion
                      defaultExpanded={false}
                      sx={{
                        boxShadow: 'none',
                        border: 'none',
                        '&:before': {
                          display: 'none',
                        },
                        '&.Mui-expanded': {
                          margin: 0,
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: '#48a1ad' }} />}
                        sx={{
                          px: 0,
                          '&.Mui-expanded': {
                            minHeight: '48px',
                          },
                          '& .MuiAccordionSummary-content': {
                            margin: 0,
                            '&.Mui-expanded': {
                              margin: 0,
                            },
                          },
                        }}
                      >
                        <QuestionEnglishInline
                          questionLocale={item.question_locale}
                          accent="legacy"
                          sx={{ width: '100%', pr: 1 }}
                          originalNode={
                            <Typography
                              variant="h6"
                              sx={{
                                color: '#48a1ad',
                                fontWeight: 600,
                                fontSize: '1.1rem',
                                lineHeight: 1.5,
                              }}
                            >
                              {cleanQuestionNumber(item.question_number)}.{' '}
                              {cleanQuestionText(item.full_question)}
                            </Typography>
                          }
                          englishNode={
                            item.full_question_english ? (
                              <Typography
                                variant="h6"
                                sx={{
                                  color: '#48a1ad',
                                  fontWeight: 600,
                                  fontSize: '1.1rem',
                                  lineHeight: 1.5,
                                }}
                              >
                                {item.full_question_english}
                              </Typography>
                            ) : null
                          }
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0, pt: 0 }}>
                        {/* System Generated Answer */}
                        {item.system_generated_ans && (
                          <Box sx={{ mb: 1.5, position: 'relative' }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                mb: 1,
                                color: '#333',
                              }}
                            >
                              System generated answer:
                            </Typography>
                            <Box
                              sx={{
                                border: '1px solid #d9dede',
                                borderRadius: '4px',
                                p: 2,
                                pr: 14,
                                pb: 5,
                                backgroundColor: '#f9f9f9',
                                minHeight: '60px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                              }}
                            >
                              <Typography variant="body2" sx={{ color: '#333', whiteSpace: 'pre-wrap' }}>
                                {item.system_generated_ans}
                              </Typography>
                            </Box>
                            <Box sx={{ position: 'absolute', bottom: 10, right: 10 }}>
                              <CopyAnswerWithOriginalMenu
                                text={getDisplayAnswer(item)}
                                questionLocale={item.question_locale}
                                qafId={item.qaf_id}
                                accent="legacy"
                              />
                            </Box>
                          </Box>
                        )}

                        {/* Retrieval Sources (expandable) */}
                        <Accordion
                          disableGutters
                          elevation={0}
                          sx={{
                            border: '1px solid #d9dede',
                            borderRadius: '4px',
                            mb: 1.5,
                            '&:before': { display: 'none' },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: '#48a1ad' }} />}
                            sx={{
                              minHeight: 48,
                              px: 2,
                              '&.Mui-expanded': { minHeight: 48 },
                              '& .MuiAccordionSummary-content': {
                                my: 1,
                                '&.Mui-expanded': { my: 1 },
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                              <DescriptionIcon sx={{ fontSize: 20, color: '#48a1ad' }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                Retrieval sources
                              </Typography>
                              <Chip
                                size="small"
                                label={(item.retrieval_sources?.length ?? 0).toString()}
                                sx={{
                                  height: 22,
                                  fontSize: '0.75rem',
                                  backgroundColor: '#e8f4f5',
                                  color: '#156C86',
                                }}
                              />
                              {avgRetrievalDistance && (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                  <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                                    Avg. vector distance: {avgRetrievalDistance}
                                  </Typography>
                                  <Tooltip title={AVERAGE_RETRIEVAL_DISTANCE_TOOLTIP} arrow placement="top">
                                    <IconButton
                                      size="small"
                                      aria-label="About average vector distance"
                                      onClick={(e) => e.stopPropagation()}
                                      sx={{ p: 0.25, color: '#666' }}
                                    >
                                      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                            {sortedRetrievalSources.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="caption" sx={{ color: '#666', lineHeight: 1.5, mb: 0.5 }}>
                                  {RETRIEVAL_SCORE_LEGEND}
                                </Typography>
                                {sortedRetrievalSources.map((src, idx) => (
                                  <Box
                                    key={`${src.source_display_name}-${idx}`}
                                    sx={{
                                      border: '1px solid #d9dede',
                                      borderRadius: '4px',
                                      p: 2,
                                      backgroundColor: '#fafafa',
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ color: '#333', fontWeight: 600 }}>
                                      {src.source_display_name || 'Source'}
                                      {typeof src.relevance_score === 'number' && (
                                        <Box
                                          component="span"
                                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, ml: 0.5 }}
                                        >
                                          <Typography component="span" variant="body2" sx={{ color: '#666' }}>
                                            · Vector distance: {formatRetrievalDistance(src.relevance_score)}
                                          </Typography>
                                          <Tooltip title={RETRIEVAL_DISTANCE_TOOLTIP} arrow placement="top">
                                            <IconButton
                                              size="small"
                                              component="span"
                                              aria-label="About vector distance"
                                              sx={{ p: 0.25, color: '#666' }}
                                            >
                                              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      )}
                                    </Typography>
                                    {src.text_excerpt ? (
                                      <Typography variant="body2" sx={{ color: '#666', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                        {src.text_excerpt}
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" sx={{ color: '#999', mt: 0.5 }}>
                                        No excerpt available.
                                      </Typography>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ color: '#999' }}>
                                No sources retrieved for this question.
                              </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>

                        {/* User Edited Answer */}
                        {item.user_edited_ans && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                mb: 1,
                                color: '#333',
                              }}
                            >
                              User edited answer:
                            </Typography>
                            <Box
                              sx={{
                                border: '1px solid #d9dede',
                                borderRadius: '4px',
                                p: 2,
                                backgroundColor: '#ffffff',
                                minHeight: '60px',
                                maxHeight: '150px',
                                overflowY: 'auto',
                              }}
                            >
                              <Typography variant="body2" sx={{ color: '#333', whiteSpace: 'pre-wrap' }}>
                                {item.user_edited_ans}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Admin Answer Section - Show if admin_edited_ans exists or edit mode is on */}
                        {(item.admin_edited_ans || editModes[item.id]) && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                mb: 1,
                                color: '#333',
                              }}
                            >
                              {item.admin_edited_ans ? 'Admin edited answer:' : 'Answer:'}
                            </Typography>
                            {editModes[item.id] ? (
                              <Box sx={{ position: 'relative' }}>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={6}
                                  value={editedAnswers[item.id] || getDisplayAnswer(item)}
                                  onChange={(e) => handleAnswerChange(item.id, e.target.value)}
                                  variant="outlined"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      fontSize: '0.95rem',
                                      lineHeight: 1.5,
                                      '& fieldset': {
                                        borderColor: '#d9dede',
                                      },
                                      '&:hover fieldset': {
                                        borderColor: '#47A1AD',
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: '#47A1AD',
                                      },
                                    },
                                    '& .MuiInputBase-input': {
                                      paddingTop: '12px',
                                      paddingBottom: '45px',
                                      whiteSpace: 'pre-wrap',
                                    },
                                  }}
                                />
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    bottom: 8,
                                    right: 8,
                                    zIndex: 1,
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleSaveAnswer(item)}
                                    disabled={savingAnswers[item.id]}
                                    sx={{
                                      textTransform: 'none',
                                      fontSize: '0.875rem',
                                      backgroundColor: '#47A1AD',
                                      '&:hover': {
                                        backgroundColor: '#156C86',
                                      },
                                      '&:disabled': {
                                        backgroundColor: '#cccccc',
                                        color: '#666666',
                                      },
                                    }}
                                  >
                                    {savingAnswers[item.id] ? 'Saving...' : 'Save'}
                                  </Button>
                                </Box>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  border: '1px solid #d9dede',
                                  borderRadius: '4px',
                                  p: 2,
                                  backgroundColor: '#e8f5e9',
                                  minHeight: '60px',
                                  maxHeight: '200px',
                                  overflowY: 'auto',
                                }}
                              >
                                <Typography variant="body2" sx={{ color: '#333', whiteSpace: 'pre-wrap' }}>
                                  {item.admin_edited_ans}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}

                        {/* Feedback Reason */}
                        {item.feedback_reason && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                mb: 1,
                                color: '#333',
                              }}
                            >
                              Feedback reason:
                            </Typography>
                            <Box
                              sx={{
                                border: '1px solid #d9dede',
                                borderRadius: '4px',
                                p: 2,
                                backgroundColor: '#ffffff',
                                minHeight: '40px',
                              }}
                            >
                              <Typography variant="body2" sx={{ color: '#666', whiteSpace: 'pre-wrap' }}>
                                {item.feedback_reason}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mt: 2, position: 'relative' }}>
                          {/* Left side: Lock icon and Edit mode toggle (hidden for approved and archived items) */}
                          {item.admin_status?.toUpperCase() !== 'APPROVED' && item.admin_status?.toUpperCase() !== 'ARCHIVED' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {editModes[item.id] ? (
                                <LockOpenIcon sx={{ color: '#47A1AD', fontSize: 20 }} />
                              ) : (
                                <LockIcon sx={{ color: '#999999', fontSize: 20 }} />
                              )}
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={editModes[item.id] || false}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        handleEditModeToggle(item.id, getDisplayAnswer(item));
                                      } else {
                                        handleEditModeToggle(item.id, getDisplayAnswer(item));
                                      }
                                    }}
                                    disabled={updatingId === item.id}
                                    size="small"
                                    sx={{
                                      '& .MuiSwitch-switchBase': {
                                        color: '#ffffff',
                                        '&.Mui-checked': {
                                          color: '#ffffff',
                                        },
                                      },
                                      '& .MuiSwitch-thumb': {
                                        backgroundColor: '#ffffff',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                      },
                                      '& .MuiSwitch-track': {
                                        backgroundColor: '#d0d0d0',
                                        opacity: 1,
                                      },
                                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#47A1AD',
                                        opacity: 1,
                                      },
                                      '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                                        backgroundColor: '#ffffff',
                                      },
                                      '&.Mui-disabled': {
                                        opacity: 0.5,
                                      },
                                    }}
                                  />
                                }
                                label="Edit mode"
                                sx={{
                                  margin: 0,
                                  '& .MuiFormControlLabel-label': {
                                    fontSize: '0.875rem',
                                    color: '#000000',
                                    fontWeight: 400,
                                  },
                                  '& .Mui-disabled .MuiFormControlLabel-label': {
                                    color: '#cccccc',
                                  },
                                }}
                              />
                            </Box>
                          )}

                          {/* Right side: Archive (for non-approved items) */}
                          {item.admin_status?.toUpperCase() !== 'ARCHIVED' && item.admin_status?.toUpperCase() !== 'APPROVED' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Button
                                variant="text"
                                color="primary"
                                startIcon={<ArchiveIconSVG />}
                                onClick={() => handleArchiveClick(item.qaf_id, item.id)}
                                disabled={updatingId === item.id}
                                sx={{
                                  textTransform: 'none',
                                  px: 2,
                                  backgroundColor: 'transparent',
                                  '&:hover': {
                                    backgroundColor: 'rgba(72, 161, 173, 0.1)',
                                  },
                                  '&:disabled': {
                                    color: '#cccccc',
                                    backgroundColor: 'transparent',
                                  },
                                  '& .MuiButton-startIcon': {
                                    marginRight: '8px',
                                  },
                                }}
                              >
                                Archive
                              </Button>
                              {/* Approve Checkbox (only for non-approved, non-archived items) */}
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={approvedItems.has(item.id) || item.admin_status?.toUpperCase() === 'APPROVED'}
                                    onChange={handleApproveCheckboxChange(item.id, item.qaf_id)}
                                    disabled={updatingId === item.id}
                                    sx={{
                                      color: '#47A1AD',
                                      '&.Mui-checked': {
                                        color: '#47A1AD',
                                      },
                                      '&.Mui-disabled': {
                                        color: '#cccccc',
                                      },
                                    }}
                                  />
                                }
                                label="Approve"
                                sx={{
                                  margin: 0,
                                  '& .MuiFormControlLabel-label': {
                                    color: '#47A1AD',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                  },
                                  '& .Mui-disabled .MuiFormControlLabel-label': {
                                    color: '#cccccc',
                                  },
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                        
                        {/* Approve Checkbox at bottom right (for approved items) - only shows when accordion is expanded */}
                        {item.admin_status?.toUpperCase() === 'APPROVED' && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={true}
                                  onChange={handleDisapproveCheckboxChange(item.id, item.qaf_id)}
                                  disabled={updatingId === item.id}
                                  sx={{
                                    color: '#47A1AD',
                                    '&.Mui-checked': {
                                      color: '#47A1AD',
                                    },
                                    '&.Mui-disabled': {
                                      color: '#cccccc',
                                    },
                                  }}
                                />
                              }
                              label="Approve"
                              sx={{
                                margin: 0,
                                '& .MuiFormControlLabel-label': {
                                  color: '#47A1AD',
                                  fontSize: '0.875rem',
                                  fontWeight: 500,
                                },
                                '& .Mui-disabled .MuiFormControlLabel-label': {
                                  color: '#cccccc',
                                },
                              }}
                            />
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </CardContent>
                </Card>
              );
              })}
            </Box>

            {/* Pagination */}
            <Paper
              elevation={0}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 0,
                backgroundColor: '#ffffff',
              }}
            >
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    px: 2,
                    py: 1,
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.875rem',
                  },
                }}
              />
            </Paper>
          </>
        )}

      {/* Toast Notification */}
      <Snackbar
        open={toastMessage !== null}
        autoHideDuration={3000}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setToastMessage(null)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={archiveDialogOpen}
        onClose={handleArchiveCancel}
        aria-labelledby="archive-dialog-title"
        aria-describedby="archive-dialog-description"
      >
        <DialogTitle id="archive-dialog-title">
          Archive Item
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="archive-dialog-description">
            Are you sure you want to archive this item? Archived items can be viewed in the Archived filter.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleArchiveCancel} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleArchiveConfirm} 
            variant="contained"
            color="primary"
            autoFocus
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default ReviewAnswers;
